import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { AumFinancialNumberPipe } from '@modeso/aumico-lib-core-fe';
import { I18NextPipe } from 'angular-i18next';
import { AccountStatus } from '../accounts-overview/accounts-overview.types';
import { isoDateLabel, quoteLineOf, quoteTextOf } from '../../presenters/document-feedback-keys';
import {
    AccountReasonFormat,
    AccountReasonLine,
    accountCardReasonKeyOf,
    accountCardStepKeyOf,
    accountReasonLineOf,
} from '../../presenters/account-card-reason';
import { documentKindLabelKeyOf } from '../../presenters/document-ai-state';
import {
    AccountAiReading,
    AccountHeader,
    AccountReconciliation,
    Money,
    ManualBalanceEntry,
    ManualBalanceEntryValue,
    PaneKind,
    PaneKindChange,
    PaneKindOption,
    ReviewDialogState,
    WorkspacePane,
} from './account-workspace.types';

const STATUS_VARIANTS: Record<AccountStatus, 'green' | 'red' | 'gray'> = {
    reconciled: 'green',
    difference: 'red',
    open: 'gray',
};

const STATUS_LABEL_KEYS: Record<AccountStatus, string> = {
    reconciled: 'abcp_accounts_status_reconciled',
    difference: 'abcp_accounts_status_difference',
    open: 'abcp_accounts_status_open',
};

const PANE_KIND_LABEL_KEYS: Record<PaneKind, string> = {
    ledger: 'abcp_workspace_pane_kind_ledger',
    documents: 'abcp_workspace_pane_kind_documents',
    comments: 'abcp_workspace_pane_kind_comments',
    todos: 'abcp_workspace_pane_kind_todos',
    adjustments: 'abcp_workspace_pane_kind_adjustments',
    activityLog: 'abcp_workspace_pane_kind_activity_log',
};

const PANE_KINDS: PaneKind[] = ['ledger', 'documents', 'comments', 'todos', 'adjustments', 'activityLog'];
// 280, not 320: the page opens with FOUR areas, and four times 320 plus borders no longer
// fits the content column of a 1440px screen, so they scrolled sideways instead of getting narrower.
const MIN_PANE_WIDTH = 280;

//REV-21361 — Account detail shell: header, reconciliation row, pane bar and the pane columns.

@Component({
    selector: 'abcp-account-workspace',
    templateUrl: './account-workspace.component.html',
    styleUrls: ['./account-workspace.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountWorkspaceComponent implements OnChanges {
    @Input() account: AccountHeader | null = null;
    @Input() reconciliation: AccountReconciliation | null = null;
    @Input() manualEntry: ManualBalanceEntry | null = null;   // null = row closed
    @Input() reviewMarked = false;
    @Input() reviewDialog: ReviewDialogState | null = null;   // null = dialog closed
    @Input() panes: WorkspacePane[] = [];
    @Input() paneContent: TemplateRef<{ $implicit: WorkspacePane }> | null = null;
    @Input() loading = false;
    /** The account is on screen from the store, its fresh state not yet in: the card shows no old status. */
    @Input() aiCardLoading = false;
    @Input() error = false;
    /** Which of the two things `error` can mean — drives `data-reason` and the sentence shown. */
    @Input() errorReason: 'not-found' | 'load-failed' = 'load-failed';
    /**
     * REV-22005 — a write is in flight (review flag or document value). The confirm buttons go quiet
     * so a second click cannot send the same write twice; the dialog and the entry row stay open.
     */
    @Input() saving = false;
    /** REV-22005 — the entry row's failure, already translated by the host. `null` = none. */
    @Input() entryError: string | null = null;
    /**
     * REV-22005 — the review write's failure, already translated. Shown in the dialog when it is open,
     * and under the review button otherwise (taking the marker off has no dialog).
     */
    @Input() reviewError: string | null = null;
    /** Shows the review button (and its error line). `false` hides it while review is not offered. */
    @Input() showReview = true;
    /** Shows the `Zurück` button in the head. `false` when the page header carries the back button. */
    @Input() showBack = true;

    @Output() reviewMarkOpen = new EventEmitter<void>();
    @Output() reviewMarkConfirm = new EventEmitter<string>();
    @Output() reviewMarkCancel = new EventEmitter<void>();
    @Output() reviewUnmark = new EventEmitter<void>();
    @Output() backSelect = new EventEmitter<void>();
    @Output() manualEntryOpen = new EventEmitter<void>();
    @Output() manualEntryConfirm = new EventEmitter<ManualBalanceEntryValue>();
    @Output() manualEntryCancel = new EventEmitter<void>();
    @Output() paneAdd = new EventEmitter<void>();
    @Output() paneKindChange = new EventEmitter<PaneKindChange>();
    @Output() paneClose = new EventEmitter<string>();
    @Output() paneReorder = new EventEmitter<string[]>();

    @ViewChild('paneStrip') private paneStrip?: ElementRef<HTMLElement>;
    @ViewChildren('paneElement') private paneElements!: QueryList<ElementRef<HTMLElement>>;

    readonly paneKindOptions: PaneKindOption[];
    readonly skeletonRows = [1, 2, 3];

    /** Typed values of the two entry-row fields and of the dialog comment — display state, not data. */
    amountDraft: string | number = '';
    referenceDraft = '';
    commentDraft = '';

    /** Pane base widths in px, session only: no input, no output. Empty = every pane shares equally. */
    paneWidths: Record<string, number> = {};

    /** The line under the chip, worded once per new reconciliation. `null` = no line. */
    reasonLine: AccountReasonLine | null = null;

    private resizing: {
        leftId: string;
        rightId: string | null;
        startX: number;
        leftWidth: number;
        rightWidth: number;
        leftMinWidth: number;
    } | null = null;

    private readonly reasonFormat: AccountReasonFormat = {
        kind: (kind) => {
            const key = documentKindLabelKeyOf(kind);
            return key === '' ? '—' : this.i18next.transform(key);
        },
        amount: (money) => this.moneyLabel(money, true),
        number: (money) => this.moneyLabel(money, false),
        date: (iso) => isoDateLabel(iso),
    };

    constructor(
        private readonly i18next: I18NextPipe,
        private readonly finNumber: AumFinancialNumberPipe,
    ) {
        this.paneKindOptions = PANE_KINDS.map((kind) => ({ id: kind, label: this.paneKindLabel(kind) }));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['reconciliation']) {
            this.reasonLine =
                this.reconciliation == null ? null : accountReasonLineOf(this.reconciliation, this.reasonFormat);
        }
        if (changes['manualEntry']) {
            this.amountDraft = this.manualEntry?.amount ?? '';
            this.referenceDraft = this.manualEntry?.reference ?? '';
        }
        if (changes['reviewDialog']) {
            this.commentDraft = this.reviewDialog?.comment ?? '';
        }
    }

    /** Zero is a value; only an empty field is missing. */
    get amountMissing(): boolean {
        return this.amountDraft == null || this.amountDraft === '';
    }

    /**
     * An empty field confirms the REMOVAL of the document value — so it can be confirmed only while
     * there is a value to remove. Without one there is nothing to confirm.
     */
    get confirmDisabled(): boolean {
        return this.amountMissing && this.reconciliation?.documentBalance == null;
    }

    get amountLabel(): string {
        const currency = this.reconciliation?.documentBalance?.currency ?? this.reconciliation?.erpBalance?.currency;
        const amount = this.i18next.transform('abcp_workspace_amount');
        return currency ? `${amount} ${currency}` : amount;
    }

    statusVariant(status: AccountStatus): 'green' | 'red' | 'gray' {
        return STATUS_VARIANTS[status];
    }

    statusLabelKey(status: AccountStatus): string {
        return STATUS_LABEL_KEYS[status];
    }

    /** Where a read value comes from, shown on hover: date, the quoted line, page and line. */
    readingHint(reading: AccountAiReading | null | undefined): string {
        if (reading?.state !== 'VALUE') {
            return '';
        }
        const [year, month, day] = (reading.asOfDate ?? '').split('-');
        const position = quoteLineOf(reading.line);
        return [
            reading.asOfDate ? this.i18next.transform('abcp_workspace_ai_as_of', { date: `${day}.${month}.${year}` }) : '',
            reading.quote ? this.i18next.transform('abcp_document_quote', { quote: quoteTextOf(reading.quote) }) : '',
            position ? this.i18next.transform('abcp_document_quote_line', position) : '',
        ]
            .filter((part) => part !== '')
            .join(' · ');
    }

    aiReasonKey(reason: string | undefined): string {
        return accountCardReasonKeyOf(reason);
    }

    /** The step the AI is in, labelled as in the documents list; the balance reading when unknown. */
    aiStepKey(reading: AccountAiReading | null | undefined): string {
        return accountCardStepKeyOf(reading?.step);
    }

    /** Formatted like the card's other figures; `—` without a value. */
    private moneyLabel(money: Money | null | undefined, withCurrency: boolean): string {
        if (money == null) {
            return '—';
        }
        const amount = this.finNumber.transform(money.amount, '1.2-2');
        return withCurrency && money.currency ? `${money.currency} ${amount}` : amount;
    }

    paneKindLabel(kind: PaneKind): string {
        return this.i18next.transform(PANE_KIND_LABEL_KEYS[kind]);
    }

    /**
     * The divider on the right edge of the last column can only pull width back, so it earns its
     * place only while the area scrolls sideways. It stays put during a drag: at the stop the area
     * stops scrolling, and the grip must not vanish from under the cursor.
     */
    get showEndDivider(): boolean {
        if (this.resizing != null) {
            return true;
        }
        const strip = this.paneStrip?.nativeElement;
        return strip != null && strip.scrollWidth > strip.clientWidth;
    }

    /** `1 1 0` shares the area; a frozen pane keeps its px base and must not shrink (see onResizeStart). */
    paneFlex(pane: WorkspacePane): string {
        const width = this.paneWidths[pane.id];
        return width == null ? '1 1 0' : `1 0 ${width}px`;
    }

    trackByPaneId(_index: number, pane: WorkspacePane): string {
        return pane.id;
    }

    onPaneKindChange(id: string, kind: PaneKind): void {
        this.paneKindChange.emit({ id, kind });
    }

    onPaneDrop(event: CdkDragDrop<WorkspacePane[]>): void {
        const ids = this.panes.map((pane) => pane.id);
        moveItemInArray(ids, event.previousIndex, event.currentIndex);
        this.paneReorder.emit(ids);
    }

    onConfirmManualEntry(): void {
        this.manualEntryConfirm.emit({
            amount: this.amountMissing ? null : Number(this.amountDraft),
            reference: this.referenceDraft,
        });
    }

    /**
     * Every pane is frozen at its measured width so the ones away from the divider are left alone:
     * with `flex-shrink: 1` they would give way as soon as the pair needs more than the area.
     */
    onResizeStart(event: MouseEvent, index: number): void {
        const left = this.panes[index];
        if (left == null) {
            return;
        }
        // The divider on the right edge of the last column has no neighbour to work with.
        const right = this.panes[index + 1] ?? null;

        event.preventDefault();

        this.paneElements.forEach((element, position) => {
            const pane = this.panes[position];
            if (pane) {
                this.paneWidths[pane.id] = element.nativeElement.getBoundingClientRect().width;
            }
        });

        this.resizing = {
            leftId: left.id,
            rightId: right?.id ?? null,
            startX: event.clientX,
            leftWidth: this.paneWidths[left.id],
            rightWidth: right ? this.paneWidths[right.id] : 0,
            // Without a neighbour the freed width would be no one's, and flex-grow would spread it
            // over every column. So the last column stops where the columns just fill the area.
            leftMinWidth: right ? MIN_PANE_WIDTH : Math.max(MIN_PANE_WIDTH, this.areaLeftOver(left.id)),
        };
    }

    /** Width the area has left over for one pane once all the others have taken theirs. */
    private areaLeftOver(paneId: string): number {
        const others = this.panes.reduce(
            (total, pane) => (pane.id === paneId ? total : total + this.paneWidths[pane.id]),
            0,
        );
        return (this.paneStrip?.nativeElement.clientWidth ?? 0) - others;
    }

    /**
     * The divider moves width from one neighbour to the other, so the total stays put — the two
     * directions end differently though. To the right the right-hand pane gives way down to its
     * minimum and the left one carries on growing, which is what makes the pane area wider than the
     * space it has. To the left the divider simply stops once the left-hand pane is at its minimum:
     * there is nothing to hand over, so the right-hand one must not grow either. At the divider of
     * the last column that minimum is the point where the columns stop overflowing the area.
     */
    @HostListener('document:mousemove', ['$event'])
    onResizeMove(event: MouseEvent): void {
        if (!this.resizing) {
            return;
        }

        const { leftId, rightId, startX, leftWidth, rightWidth, leftMinWidth } = this.resizing;
        const delta = event.clientX - startX;

        if (delta >= 0) {
            this.paneWidths[leftId] = leftWidth + delta;
            if (rightId) {
                this.paneWidths[rightId] = Math.max(MIN_PANE_WIDTH, rightWidth - delta);
            }
        } else {
            const given = Math.min(-delta, leftWidth - leftMinWidth);
            this.paneWidths[leftId] = leftWidth - given;
            if (rightId) {
                this.paneWidths[rightId] = rightWidth + given;
            }
        }
    }

    @HostListener('document:mouseup')
    onResizeEnd(): void {
        this.resizing = null;
    }

    //Nobody tells a pure view that the area got narrower; this re-reads showEndDivider.
    @HostListener('window:resize')
    onWindowResize(): void {}
}
